import { useQuery } from '@tanstack/react-query'
import type { Query } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import { MyActivationStateDocument } from '@/gql/graphql'
import type { MyActivationStateQuery } from '@/gql/graphql'

export const myActivationStateKey = () => ['activation', 'myState'] as const

export type ClientActivationState = MyActivationStateQuery['myActivationState']

/**
 * Standalone queryFn for the activation state — exported so confirm hooks can
 * drive `qc.fetchQuery` without re-importing the document or graphql client.
 */
export const activationStateQueryFn = async (): Promise<ClientActivationState> => {
  const data = await fetchGql(MyActivationStateDocument, {})
  return data.myActivationState
}

interface UseMyActivationStateOptions {
  /**
   * Polling interval in ms (or a function of the query, TanStack's function form). Disabled by default.
   *
   * Use 45_000 on the ActivationWaitingScreen (NF-3, client-activation-r2 T04), EXCEPT through the ADR-015 rule:
   * a consumer that DISPLAYS a server-reported hold (the activation gate's Day-0 screen, the import lock) may pass
   * the function form of `heldPollIntervalMs` (`@/lib/onboarding/day0-view`), which returns a positive interval only
   * while a hold is on screen (10 s self-resolving / 60 s operator-resolved) and `false` otherwise. Nothing else may
   * set an interval, and `refetchOnWindowFocus` stays off app-wide.
   */
  refetchInterval?: number | false | ((query: Query<ClientActivationState>) => number | false)
  enabled?: boolean
}

export function useMyActivationState(opts: UseMyActivationStateOptions = {}) {
  const { refetchInterval = false, enabled = true } = opts

  return useQuery<ClientActivationState>({
    queryKey: myActivationStateKey(),
    queryFn: activationStateQueryFn,
    enabled,
    refetchInterval,
    staleTime: 10_000,
    gcTime: 30_000,
  })
}
